export type SecondaryBg =
  | "white"
  | "purple-00"
  | "purple-01"
  | "purple-02"
  | "purple-03"
  | "purple-04"
  | "purple-05"
  | "forest-01"
  | "forest-02"
  | "forest-03"
  | "forest-04"
  | "forest-05"
  | "grape-01"
  | "grape-02"
  | "grape-03"
  | "grape-04"
  | "grape-05"
  | "dusk-01"
  | "dusk-02"
  | "dusk-03"
  | "dusk-04"
  | "dusk-05"
  | "midnight"
  | "forest"
  | "dusk"
  | "grape"
  | "deep-purple"
  | "periwinkle"
  | "lilac";

type BgStyle = {
  bg: string;
  to: string;
  hex: string;
  onDark: boolean;
};

const BG_STYLES: Record<SecondaryBg, BgStyle> = {
  white: { bg: "bg-white", to: "to-white", hex: "#ffffff", onDark: false },
  "purple-00": { bg: "bg-purple-00", to: "to-purple-00", hex: "#e5e2fe", onDark: false },
  "purple-01": { bg: "bg-purple-01", to: "to-purple-01", hex: "#c2bbfc", onDark: false },
  "purple-02": { bg: "bg-purple-02", to: "to-purple-02", hex: "#ab9ffb", onDark: false },
  "purple-03": { bg: "bg-purple-03", to: "to-purple-03", hex: "#6a5fda", onDark: true },
  "purple-04": { bg: "bg-purple-04", to: "to-purple-04", hex: "#5037b2", onDark: true },
  "purple-05": { bg: "bg-purple-05", to: "to-purple-05", hex: "#3f2b8d", onDark: true },
  "forest-01": { bg: "bg-forest-01", to: "to-forest-01", hex: "#41816c", onDark: true },
  "forest-02": { bg: "bg-forest-02", to: "to-forest-02", hex: "#305f4f", onDark: true },
  "forest-03": { bg: "bg-forest-03", to: "to-forest-03", hex: "#1e3c32", onDark: true },
  "forest-04": { bg: "bg-forest-04", to: "to-forest-04", hex: "#20342f", onDark: true },
  "forest-05": { bg: "bg-forest-05", to: "to-forest-05", hex: "#0e1515", onDark: true },
  "grape-01": { bg: "bg-grape-01", to: "to-grape-01", hex: "#792aa7", onDark: true },
  "grape-02": { bg: "bg-grape-02", to: "to-grape-02", hex: "#5e1886", onDark: true },
  "grape-03": { bg: "bg-grape-03", to: "to-grape-03", hex: "#420b62", onDark: true },
  "grape-04": { bg: "bg-grape-04", to: "to-grape-04", hex: "#220632", onDark: true },
  "grape-05": { bg: "bg-grape-05", to: "to-grape-05", hex: "#030105", onDark: true },
  "dusk-01": { bg: "bg-dusk-01", to: "to-dusk-01", hex: "#f5eff6", onDark: false },
  "dusk-02": { bg: "bg-dusk-02", to: "to-dusk-02", hex: "#e2cfe3", onDark: false },
  "dusk-03": { bg: "bg-dusk-03", to: "to-dusk-03", hex: "#cfaed0", onDark: false },
  "dusk-04": { bg: "bg-dusk-04", to: "to-dusk-04", hex: "#bc8ebd", onDark: false },
  "dusk-05": { bg: "bg-dusk-05", to: "to-dusk-05", hex: "#a96eab", onDark: true },
  midnight: { bg: "bg-midnight", to: "to-midnight", hex: "#180658", onDark: true },
  forest: { bg: "bg-forest-03", to: "to-forest-03", hex: "#1e3c32", onDark: true },
  dusk: { bg: "bg-dusk-03", to: "to-dusk-03", hex: "#cfaed0", onDark: false },
  grape: { bg: "bg-grape-03", to: "to-grape-03", hex: "#420b62", onDark: true },
  "deep-purple": { bg: "bg-purple-05", to: "to-purple-05", hex: "#3f2b8d", onDark: true },
  periwinkle: { bg: "bg-purple-02", to: "to-purple-02", hex: "#ab9ffb", onDark: false },
  lilac: { bg: "bg-purple-01", to: "to-purple-01", hex: "#c2bbfc", onDark: false },
};

// Built from BG_STYLES so it stays in sync.
const HEX_TO_SLUG: Record<string, SecondaryBg> = (() => {
  const map: Record<string, SecondaryBg> = {};
  for (const [slug, style] of Object.entries(BG_STYLES)) {
    map[style.hex.toLowerCase()] = slug as SecondaryBg;
  }
  return map;
})();

// Accepts either the legacy string slug ("forest-03") or storyblok-palette
// plugin output ({ value: "#1e3c32" }), so old content keeps rendering.
export function resolveBg(value: unknown, fallback: SecondaryBg): BgStyle {
  let slug: SecondaryBg | undefined;
  if (typeof value === "string") {
    slug = value as SecondaryBg;
  } else if (value && typeof value === "object" && "value" in value) {
    const hex = (value as { value?: unknown }).value;
    if (typeof hex === "string") {
      slug = HEX_TO_SLUG[hex.toLowerCase()];
    }
  }
  return BG_STYLES[slug || fallback] || BG_STYLES[fallback];
}
